// Phase 32 Plan 32-04 Task 2 — QuillTargetOverlay (RUNE-35).
//
// Pixi overlay above LetterLayer that highlights eligible target cells for
// Quill's Mark or Claim mode. Composition mirrors AnchorPlacementOverlay
// (Phase 31 Plan 31-03 Task 1) — copy of the EligibleCellOverlay sub-
// component with mode-specific eligibility predicates. Per CONTEXT (UI-SPEC
// §1 row 6 — Claude's Discretion): planner picks the simplest reuse path.
// We duplicate the sub-component locally rather than extract a shared
// helper — duplication is cheaper than premature coupling here.
//
// Mount lifecycle: parent (MatchScreen) sets `quillMode` to 'mark' or
// 'claim' after QuillModePicker.onSelect fires. The overlay reads
// playerView.board to derive eligibles, renders one EligibleCellOverlay
// per match, and dispatches the user's tap via onTap(cellIdx). The parent
// then commits setPhaseB({mode, target_cell_idx}) and onSubmit().
//
// Eligibility predicates (UI-SPEC §1 row 6 + plan <interfaces>):
//   - Mark : c.is_anchor === true && c.anchor_owner !== null
//                              && c.anchor_owner !== yourSlot
//     → highlights other-slot Anchors only. B12 firewall: derived from
//       playerView.board (public-by-construction) NOT from any private
//       per-slot projection field.
//   - Claim: c.is_anchor === false && c.is_hollow === false
//                              && c.tier === 'common'
//                              && isAdjacentToOwnAnchor(c.cell_idx)
//     → highlights Common-tier non-Anchor non-Hollow cells adjacent to
//       caller's own Anchors.
//
// Locks:
//   - Pitfall 5 (UI-SPEC §13 #8): Group.shared exclusively for any
//     Tweedle animation; no private Group instantiation.
//   - B12 firewall (UI-SPEC §13 #10): no private-field reads for
//     affinity letters.

import { useStore } from '@nanostores/react'
import { useApplication } from '@pixi/react'
import type { Graphics } from 'pixi.js'
import { Rectangle } from 'pixi.js'
import { type JSX, useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Easing, Group, Tween } from 'tweedle.js'
import {
  BOARD_COLS,
  boardRows,
  CELL_SIZE_PX,
  cellIdxToScreen,
} from '../../game/layers/board-layout'
import type { PlayerView } from '../../game/PlayerViewSchema'
import { installTweedleTickerDriver } from '../../game/theme/tweedle-bootstrap'
import { $reducedMotion } from '../../stores/motion'

/** Sun-Yellow accent — UI-SPEC §3 token --color-cta-primary (hex form). */
const SUN_YELLOW_HEX = 0xffc53d

/** Scale-pulse half-cycle (matches AnchorPlacementOverlay). */
const PULSE_HALF_MS = 500

/** Adjacency offsets for an 8-neighbour stencil (orthogonal + diagonal). */
function adjacencyOffsets(cols: number): ReadonlyArray<number> {
  return [-cols - 1, -cols, -cols + 1, -1, 1, cols - 1, cols, cols + 1]
}

/** Returns true if `cellIdx` is an 8-adjacent neighbour of any cell in
 *  `ownAnchorCells`, with row/col wrap-around guards. */
function isAdjacentTo(
  cellIdx: number,
  ownAnchorCells: ReadonlySet<number>,
  cols: number = BOARD_COLS,
): boolean {
  const col = cellIdx % cols
  const row = Math.floor(cellIdx / cols)
  for (const off of adjacencyOffsets(cols)) {
    const nIdx = cellIdx + off
    if (nIdx < 0) continue
    const nCol = nIdx % cols
    const nRow = Math.floor(nIdx / cols)
    // Wrap-around guard: |Δcol| or |Δrow| > 1 means the neighbour is on the
    // wrong side of the grid (math wrap of mod and division).
    if (Math.abs(nCol - col) > 1) continue
    if (Math.abs(nRow - row) > 1) continue
    if (ownAnchorCells.has(nIdx)) return true
  }
  return false
}

export interface QuillTargetOverlayProps {
  readonly mode: 'mark' | 'claim'
  readonly playerView: PlayerView
  readonly yourSlot: 0 | 1
  readonly onTap: (cellIdx: number) => void
  readonly onDismiss: () => void
}

export function QuillTargetOverlay({
  mode,
  playerView,
  yourSlot,
  onTap,
  // onDismiss is consumed by the parent (Escape key + Cancel button live in
  // MatchScreen — this overlay only handles cell taps). Accepted to keep the
  // prop interface symmetrical with QuillModePicker.
  onDismiss: _onDismiss,
}: QuillTargetOverlayProps): JSX.Element {
  const intl = useIntl()

  // P5 lock — install ticker driver once per Pixi Application. Idempotent
  // (Plan 30-01 contract).
  const application = useApplication() as { app?: { ticker?: unknown } }
  useEffect(() => {
    const ticker = application?.app?.ticker as
      | Parameters<typeof installTweedleTickerDriver>[0]
      | undefined
    if (!ticker) return undefined
    const teardown = installTweedleTickerDriver(ticker)
    return teardown
  }, [application])

  const reducedMotion = useStore($reducedMotion)

  // Compute mode-specific eligibles. useMemo keeps the filter cheap on
  // re-renders driven by parent state churn.
  const eligibleCells = useMemo(() => {
    if (mode === 'mark') {
      // Mark: opponent Anchors only. B12 firewall: derived from board
      // (public-by-construction), NOT from opponent.* private fields.
      return playerView.board.filter(
        (c) =>
          c.is_anchor === true &&
          c.anchor_owner !== null &&
          c.anchor_owner !== yourSlot,
      )
    }
    // Claim: Common-tier non-Anchor non-Hollow cells adjacent to own Anchors.
    const ownAnchorCells = new Set(
      playerView.board
        .filter((c) => c.is_anchor === true && c.anchor_owner === yourSlot)
        .map((c) => c.cell_idx),
    )
    return playerView.board.filter(
      (c) =>
        c.is_anchor === false &&
        c.is_hollow === false &&
        c.tier === 'common' &&
        isAdjacentTo(c.cell_idx, ownAnchorCells),
    )
  }, [mode, playerView.board, yourSlot])

  const handleTap = useCallback(
    (cellIdx: number): void => {
      onTap(cellIdx)
    },
    [onTap],
  )

  const promptKey =
    mode === 'mark'
      ? 'match.quill.target.mark.prompt'
      : 'match.quill.target.claim.prompt'
  const promptText = intl.formatMessage({ id: promptKey })

  // Pixi-only output. The DOM prompt banner lives in <QuillTargetPrompt>
  // (exported separately) which callers MUST mount as a DOM sibling of the
  // parent Pixi <Application>. Mixing DOM elements with a <pixiContainer>
  // inside a single fragment crashes the pixi-react reconciler with "Text
  // instances are not yet supported" — discovered during v1.2 own-machine
  // UAT 2026-05-18; this overlay had previously rendered NOTHING in
  // production (silent failure) because of that mixed-tree crash.
  void promptText
  return (
    <pixiContainer
      label="QuillTargetOverlay"
      data-pixi-label="QuillTargetOverlay"
    >
      {eligibleCells.map((cell) => {
        const { screen_x, screen_y } = cellIdxToScreen(
          cell.cell_idx,
          yourSlot,
          boardRows(playerView.board.length),
        )
        return (
          <EligibleCellOverlay
            key={`quill-target-${mode}-${cell.cell_idx}`}
            cellIdx={cell.cell_idx}
            x={screen_x * CELL_SIZE_PX}
            y={screen_y * CELL_SIZE_PX}
            reducedMotion={reducedMotion}
            onTap={handleTap}
          />
        )
      })}
    </pixiContainer>
  )
}

/** DOM-only prompt banner. MUST be mounted as a DOM sibling of the parent
 *  <Application>, NOT inside it (pixi-react cannot render plain DOM nodes
 *  inside its reconciler tree). UI-SPEC §10 caption: Manrope 11px, sun-
 *  yellow border, surface-mid background. */
export function QuillTargetPrompt({
  mode,
}: {
  readonly mode: 'mark' | 'claim'
}): JSX.Element {
  const intl = useIntl()
  const promptKey =
    mode === 'mark'
      ? 'match.quill.target.mark.prompt'
      : 'match.quill.target.claim.prompt'
  const promptText = intl.formatMessage({ id: promptKey })
  return (
    <div
      data-quill-target-prompt={mode}
      style={{
        position: 'fixed',
        top: 'var(--space-5)',
        left: 'var(--space-4)',
        right: 'var(--space-4)',
        padding: 'var(--space-2) var(--space-4)',
        background: 'var(--color-surface-mid)',
        color: 'var(--color-text-primary)',
        border: '2px solid var(--color-cta-primary)',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'Manrope, system-ui, sans-serif',
        fontWeight: 600,
        fontSize: 'var(--font-size-11)',
        textAlign: 'center',
        zIndex: 40,
        pointerEvents: 'none',
      }}
    >
      {promptText}
    </div>
  )
}

interface EligibleCellOverlayProps {
  readonly cellIdx: number
  readonly x: number
  readonly y: number
  readonly reducedMotion: boolean
  readonly onTap: (cellIdx: number) => void
}

/** Pixi sub-component drawing the Sun-Yellow 3px border + 1.0 ↔ 1.2 scale-
 *  pulse for each eligible cell. Duplicates the AnchorPlacementOverlay
 *  internal sub-component (see file-level comment on the reuse rationale). */
function EligibleCellOverlay({
  cellIdx,
  x,
  y,
  reducedMotion,
  onTap,
}: EligibleCellOverlayProps): JSX.Element {
  const containerRef = useRef<{ scale?: { x: number; y: number } } | null>(null)
  const tweenRef = useRef<Tween<{ x: number; y: number }> | null>(null)

  useEffect(() => {
    if (reducedMotion) return undefined
    const target = containerRef.current?.scale
    if (!target) return undefined
    const tween = new Tween(target)
      .to({ x: 1.2, y: 1.2 }, PULSE_HALF_MS)
      .easing(Easing.Sinusoidal.InOut)
      .yoyo(true)
      .repeat(Number.POSITIVE_INFINITY)
      .delay((cellIdx % 8) * 50)
    Group.shared.add(tween)
    tween.start(Group.shared.now())
    tweenRef.current = tween
    return () => {
      tween.stop()
      tweenRef.current = null
    }
  }, [reducedMotion, cellIdx])

  const handlePointerDown = useCallback(() => {
    onTap(cellIdx)
  }, [cellIdx, onTap])

  return (
    <pixiContainer
      ref={containerRef as never}
      x={x}
      y={y}
      eventMode="static"
      cursor="pointer"
      hitArea={new Rectangle(0, 0, CELL_SIZE_PX, CELL_SIZE_PX)}
      onPointerDown={handlePointerDown}
      data-eligible-cell-idx={cellIdx}
    >
      <pixiGraphics
        draw={(g: Graphics) => {
          g.clear()
          g.rect(2, 2, CELL_SIZE_PX - 4, CELL_SIZE_PX - 4).stroke({
            width: 3,
            color: SUN_YELLOW_HEX,
            alpha: 1,
          })
        }}
      />
    </pixiContainer>
  )
}
